import { takeLatest, call, put, all } from "redux-saga/effects";
import request from "utils/api";
import {
  getMessageTemplatesRequest,
  getMessageTemplatesSuccess,
  getMessageTemplatesFailure,
  getMessageTemplateByIdRequest,
  getMessageTemplateByIdSuccess,
  getMessageTemplateByIdFailure,
  createMessageTemplateRequest,
  createMessageTemplateSuccess,
  createMessageTemplateFailure,
  updateMessageTemplateRequest,
  updateMessageTemplateSuccess,
  updateMessageTemplateFailure,
  deleteMessageTemplateRequest,
  deleteMessageTemplateSuccess,
  deleteMessageTemplateFailure,
  getActiveTemplatesRequest,
  getActiveTemplatesSuccess,
  getActiveTemplatesFailure,
  getTemplatesByCategoryRequest,
  getTemplatesByCategorySuccess,
  getTemplatesByCategoryFailure,
  searchTemplatesRequest,
  searchTemplatesSuccess,
  searchTemplatesFailure,
} from "./slice";
import i18n from "i18n";
import { notification } from "antd";

// Get all templates
function* getMessageTemplatesSaga(action) {
  try {
    const { page = 1, limit = 10, category, isActive } = action.payload || {};
    let url = `/api/admin/message-templates?page=${page}&limit=${limit}`;
    
    if (category) {
      url += `&category=${category}`;
    }
    if (isActive !== null && isActive !== undefined) {
      url += `&isActive=${isActive}`;
    }

    const { data } = yield call(request, url, {}, { method: "GET" });
    console.log('GET TEMPLATES API RESPONSE:', data);
    
    // Handle different possible response structures
    let mappedData;
    
    if (data && data.data && data.pagination) {
      // Response has { data: [...], pagination: {...} } structure
      mappedData = {
        docs: data.data,
        totalDocs: data.pagination.total,
        page: data.pagination.page,
        limit: data.pagination.limit,
        totalPages: data.pagination.pages,
        hasNextPage: data.pagination.hasNextPage,
        hasPrevPage: data.pagination.hasPrevPage,
      };
    } else if (Array.isArray(data)) {
      // Response is directly an array
      mappedData = {
        docs: data,
        totalDocs: data.length,
        page: 1,
        limit: 10,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      };
    } else if (data && data.docs) {
      // Response already has the expected structure
      mappedData = data;
    } else {
      // Fallback for unexpected structure
      console.warn('Unexpected API response structure:', data);
      mappedData = {
        docs: [],
        totalDocs: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      };
    }
    
    console.log('MAPPED DATA:', mappedData);
    yield put(getMessageTemplatesSuccess(mappedData));
  } catch (error) {
    yield put(getMessageTemplatesFailure(error.message));
    notification.error({
      message: i18n.t("alert.error"),
      description: error.message || "Không thể tải danh sách template",
    });
  }
}

// Get template by ID
function* getMessageTemplateByIdSaga(action) {
  try {
    const { id } = action.payload;
    const { data } = yield call(request, `/api/admin/message-templates/${id}`, {}, { method: "GET" });
    yield put(getMessageTemplateByIdSuccess(data));
  } catch (error) {
    yield put(getMessageTemplateByIdFailure(error.message));
    notification.error({
      message: i18n.t("alert.error"),
      description: error.message || "Không thể tải chi tiết template",
    });
  }
}

// Create template
function* createMessageTemplateSaga(action) {
  try {
    console.log('CREATE TEMPLATE SAGA - Payload:', action.payload);
    const { data } = yield call(request, "/api/admin/message-templates", action.payload);
    yield put(createMessageTemplateSuccess(data));
    notification.success({
      message: "Thành công",
      description: "Tạo template thành công",
    });
  } catch (error) {
    yield put(createMessageTemplateFailure(error.message));
    notification.error({
      message: i18n.t("alert.error"),
      description: error.message || "Không thể tạo template",
    });
  }
}

// Update template
function* updateMessageTemplateSaga(action) {
  try {
    const { id, ...data } = action.payload;
    const { data: responseData } = yield call(request, `/api/admin/message-templates/${id}`, data, { method: "PUT" });
    yield put(updateMessageTemplateSuccess(responseData));
    notification.success({
      message: "Thành công",
      description: "Cập nhật template thành công",
    });
  } catch (error) {
    yield put(updateMessageTemplateFailure(error.message));
    notification.error({
      message: i18n.t("alert.error"),
      description: error.message || "Không thể cập nhật template",
    });
  }
}

// Delete template
function* deleteMessageTemplateSaga(action) {
  try {
    const { id } = action.payload;
    yield call(request, `/api/admin/message-templates/${id}`, {}, { method: "DELETE" });
    yield put(deleteMessageTemplateSuccess({ id }));
    notification.success({
      message: "Thành công",
      description: "Xóa template thành công",
    });
  } catch (error) {
    yield put(deleteMessageTemplateFailure(error.message));
    notification.error({
      message: i18n.t("alert.error"),
      description: error.message || "Không thể xóa template",
    });
  }
}

// Get active templates
function* getActiveTemplatesSaga() {
  try {
    const { data } = yield call(request, "/api/admin/message-templates/active", {}, { method: "GET" });
    // Active templates endpoint might return just array or wrapped in data
    const templates = data.data ? data.data : data;
    yield put(getActiveTemplatesSuccess(templates));
  } catch (error) {
    yield put(getActiveTemplatesFailure(error.message));
    notification.error({
      message: i18n.t("alert.error"),
      description: error.message || "Không thể tải template hoạt động",
    });
  }
}

// Get templates by category
function* getTemplatesByCategorySaga(action) {
  try {
    const { category } = action.payload;
    const { data } = yield call(request, `/api/admin/message-templates/category/${category}`, {}, { method: "GET" });
    // Category templates endpoint might return just array or wrapped in data
    const templates = data.data ? data.data : data;
    yield put(getTemplatesByCategorySuccess(templates));
  } catch (error) {
    yield put(getTemplatesByCategoryFailure(error.message));
    notification.error({
      message: i18n.t("alert.error"),
      description: error.message || "Không thể tải template theo danh mục",
    });
  }
}

// Search templates
function* searchTemplatesSaga(action) {
  try {
    const { query } = action.payload;
    const { data } = yield call(request, `/api/admin/message-templates/search?q=${encodeURIComponent(query)}`, {}, { method: "GET" });
    // Search templates endpoint might return just array or wrapped in data  
    const templates = data.data ? data.data : data;
    yield put(searchTemplatesSuccess(templates));
  } catch (error) {
    yield put(searchTemplatesFailure(error.message));
    notification.error({
      message: i18n.t("alert.error"),
      description: error.message || "Không thể tìm kiếm template",
    });
  }
}

export default function* watchMessageTemplate() {
  yield all([
    takeLatest(getMessageTemplatesRequest.type, getMessageTemplatesSaga),
    takeLatest(getMessageTemplateByIdRequest.type, getMessageTemplateByIdSaga),
    takeLatest(createMessageTemplateRequest.type, createMessageTemplateSaga),
    takeLatest(updateMessageTemplateRequest.type, updateMessageTemplateSaga),
    takeLatest(deleteMessageTemplateRequest.type, deleteMessageTemplateSaga),
    takeLatest(getActiveTemplatesRequest.type, getActiveTemplatesSaga),
    takeLatest(getTemplatesByCategoryRequest.type, getTemplatesByCategorySaga),
    takeLatest(searchTemplatesRequest.type, searchTemplatesSaga),
  ]);
}